//============================================================================
// Name        : common.ts
// Purpose     : Adjustment Requests (Tenant Admin) - page data, text, requests,
//               busy buttons and toasts
// Author      : Senitron Team
// Copyright   : Copyright 2026 Senitron Corporation. All rights reserved.
//============================================================================
declare var $: any;

export interface IDict { [key: string]: any; }

export interface IPageData {
  recheckUrl: string;
  progressUrl: string;
  totalMatching: number;
  matchingParams: IDict;
  i18n: IDict;
}

// Everything the server hands the page, from the data- attributes of #ar-data.
export function readData(): IPageData {
  const el = document.getElementById("ar-data");
  const attr = (name: string): string => (el && el.getAttribute("data-" + name)) || "";
  const json = (name: string, fallback: any): any => {
    try { return JSON.parse(attr(name)) || fallback; } catch (e) { return fallback; }
  };
  return {
    recheckUrl: attr("recheck-url"),
    progressUrl: attr("progress-url"),
    totalMatching: parseInt(attr("total-matching"), 10) || 0,
    matchingParams: json("matching-params", {}),
    i18n: json("i18n", {})
  };
}

// t("selected", { count: 3 }) with the %{name} placeholders of gettext.
export function translator(table: IDict): (key: string, vars?: IDict) => string {
  return (key: string, vars?: IDict): string => {
    let text = String(table[key] || key);
    Object.keys(vars || {}).forEach((name) => { text = text.split("%{" + name + "}").join(String(vars[name])); });
    return text;
  };
}

// A POST with the page's CSRF token; the caller disables its button first and
// re-enables it in .always() (C-49).
export function post(url: string, data: any): any {
  const token = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
  return $.ajax({ url, type: "POST", data, dataType: "json", headers: { "X-CSRF-Token": token ? token.content : "" } });
}

export function errorMessage(xhr: any, fallback: string): string {
  return (xhr && xhr.responseJSON && (xhr.responseJSON.message || xhr.responseJSON.error)) || fallback;
}

// Buttons that call the server are disabled while the request runs (C-49).
export function busy(button: HTMLButtonElement, label?: string): string {
  const original = button.textContent || "";
  button.disabled = true;
  if (label) { button.textContent = label; }
  return original;
}

export function idle(button: HTMLButtonElement, label?: string): void {
  button.disabled = false;
  if (label !== undefined) { button.textContent = label; }
}

// The layout's #toast (shared/_toast), looked up when used: the page scripts
// load before the layout renders it, so utils/toast.ts cannot find it here.
let toastTimer: number | null = null;
export function toast(message: string, ok: boolean): void {
  const el = document.getElementById("toast");
  if (!el || !message) { return; }
  el.textContent = message;
  el.style.background = ok ? "#047857" : "#b91c1c";
  el.style.opacity = "1";
  el.style.transform = "translateX(-50%) translateY(0)";
  if (toastTimer) { window.clearTimeout(toastTimer); }
  toastTimer = window.setTimeout(() => {
    el.style.opacity = "0";
    el.style.transform = "translateX(-50%) translateY(20px)";
  },                             5000);
}
